import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import {
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    ComposedChart,
    Legend,
} from 'recharts';

import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';

import ChartCard from '~/components/ChartCard';
import LocaleMessage from '~/components/LocaleMessage';
import ReportPieChart from '~/components/Reports/ReportPieChart';

import { ChatCardContainer } from '../../Dashboard/styles';
import {
    buildCleanTimeChartData,
    CLEANING_REPORT_CHART_PERIOD_MSG_IDS,
    formatCleanTimeChartAxisTick,
    formatCleaningDuration,
    getCleanTimeChartLegendLabel,
    getCleaningModeLabel,
    getCleaningReportPeriodDaysCount,
    getCleaningStatusLabelMap,
    getVisibleCleaningReportChartPeriodIndices,
    resolveCleaningReportChartPeriodIdx,
} from './cleaningReportUtils';

export const MODE_FILTER = 'mode';
export const STATUS_FILTER = 'status';

function buildPieByField(list, field, labelMap) {
    if (!list || !list.length) return [];
    const counts = {};
    list.forEach(item => {
        const k = item[field];
        const key = k != null ? String(k) : '?';
        counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts).map(k => ({
        name: labelMap[k] || `${field} ${k}`,
        value: counts[k],
    }));
}

export default function CleaningReportChartsCard({
    cardColor,
    chartColor,
    pieSegmentColors,
    list,
    localeCode,
    periodDates,
    compact,
    initialPieField,
    areaChartHeight,
}) {
    const settings = useSelector(state => state.settings || null);
    const { dark_mode } = settings || {};

    const [pieField, setPieField] = useState(
        initialPieField === STATUS_FILTER ? STATUS_FILTER : MODE_FILTER
    );
    const [selectedPeriodIdx, setSelectedPeriodIdx] = useState(null);

    const modeLabelsPlain = {
        1: getCleaningModeLabel(1, localeCode),
        2: getCleaningModeLabel(2, localeCode),
    };
    const statusLabelsPlain = getCleaningStatusLabelMap(localeCode);

    const pieData =
        pieField === MODE_FILTER
            ? buildPieByField(list, 'mode', modeLabelsPlain)
            : buildPieByField(list, 'status', statusLabelsPlain);

    const filters = [
        {
            label: 'page.robots.cleaning_report.group_by_mode',
            value: MODE_FILTER,
        },
        {
            label: 'page.robots.cleaning_report.group_by_status',
            value: STATUS_FILTER,
        },
    ];

    const daysCount = useMemo(
        () => getCleaningReportPeriodDaysCount(periodDates),
        [periodDates]
    );

    const visiblePeriodIndices = useMemo(
        () =>
            getVisibleCleaningReportChartPeriodIndices(periodDates, daysCount),
        [periodDates, daysCount]
    );

    const periodIdx = useMemo(
        () =>
            resolveCleaningReportChartPeriodIdx(
                selectedPeriodIdx,
                periodDates,
                daysCount
            ),
        [selectedPeriodIdx, periodDates, daysCount]
    );

    const { data: chartData, unit: timeChartUnit } = useMemo(
        () => buildCleanTimeChartData(list, periodIdx, periodDates, localeCode),
        [list, periodIdx, periodDates, localeCode]
    );

    const timeSeriesLabel = getCleanTimeChartLegendLabel(localeCode);
    const lineColor = chartColor || cardColor;
    const chartH = compact ? areaChartHeight : 250;
    const chartColClass = compact ? 'col-lg-7 col-12' : 'col-md-8 col-12';
    const pieColClass = compact ? 'col-lg-5 col-12' : 'col-md-4 col-12';
    const selectMinWidth = compact ? 140 : 200;
    const rowMb = compact ? 'mb-1' : 'mb-3';
    const pieCustomization = compact
        ? {
              innerRadius: 22,
              outerRadius: 48,
              height: 132,
              stroke: dark_mode ? '#424242' : '#ddd',
          }
        : {
              innerRadius: 40,
              outerRadius: 70,
              stroke: dark_mode ? '#424242' : '#ddd',
          };

    return (
        <div className={`row card-row${compact ? ' mb-0' : ''}`}>
            <div className={`col-12 dash-card${compact ? ' mb-0' : ' mb-5'}`}>
                <ChatCardContainer
                    style={
                        compact ? { height: 'auto', minHeight: 0 } : undefined
                    }
                >
                    <ChartCard
                        className="animated slideInFromLeft animation-duration-3"
                        title={
                            compact ? (
                                <LocaleMessage msg="page.robots.cleaning_report.fs.chart_heading" />
                            ) : (
                                <LocaleMessage msg="page.robots.cleaning_report.chart_title" />
                            )
                        }
                        desc={
                            compact ? (
                                ''
                            ) : (
                                <LocaleMessage msg="page.robots.cleaning_report.chart_subtitle" />
                            )
                        }
                        value=""
                    >
                        <div className={`row align-items-end ${rowMb}`}>
                            <div
                                className={`${chartColClass} d-flex justify-content-end`}
                            >
                                <FormControl
                                    size="small"
                                    style={{ minWidth: selectMinWidth }}
                                >
                                    <InputLabel>
                                        <LocaleMessage msg="page.robots.cleaning_report.group_comparison.metric_label" />
                                    </InputLabel>
                                    <Select
                                        value={periodIdx}
                                        onChange={e =>
                                            setSelectedPeriodIdx(e.target.value)
                                        }
                                    >
                                        {visiblePeriodIndices.map(idx => (
                                            <MenuItem
                                                key={
                                                    CLEANING_REPORT_CHART_PERIOD_MSG_IDS[
                                                        idx
                                                    ]
                                                }
                                                value={idx}
                                            >
                                                <LocaleMessage
                                                    msg={
                                                        CLEANING_REPORT_CHART_PERIOD_MSG_IDS[
                                                            idx
                                                        ]
                                                    }
                                                />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                            <div className={pieColClass}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>
                                        <LocaleMessage msg="page.dashboard.group_by" />
                                    </InputLabel>
                                    <Select
                                        value={pieField}
                                        onChange={e =>
                                            setPieField(e.target.value)
                                        }
                                    >
                                        {filters.map(f => (
                                            <MenuItem
                                                value={f.value}
                                                key={f.value}
                                            >
                                                <LocaleMessage msg={f.label} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </div>
                        </div>
                        <div className="row">
                            <div
                                className={`${chartColClass} ${rowMb} area-chart-container`}
                            >
                                <ResponsiveContainer
                                    className="card-img-bottom overflow-hidden animated slideInFromLeft animation-duration-5"
                                    width="100%"
                                    height={chartH}
                                >
                                    <ComposedChart
                                        data={chartData}
                                        margin={{
                                            top: 10,
                                            right: 10,
                                            bottom: 0,
                                            left: compact ? 0 : 10,
                                        }}
                                    >
                                        <CartesianGrid
                                            stroke="#f5f5f5"
                                            strokeDasharray="3 3"
                                        />
                                        <XAxis
                                            dataKey="name"
                                            fontSize={compact ? 9 : 12}
                                            padding={{ left: 0, right: 0 }}
                                        />
                                        <YAxis
                                            yAxisId="left"
                                            width={compact ? 36 : 60}
                                            fontSize={compact ? 9 : 12}
                                            allowDecimals={
                                                timeChartUnit !== 'seconds'
                                            }
                                            tickFormatter={v =>
                                                formatCleanTimeChartAxisTick(
                                                    v,
                                                    timeChartUnit
                                                )
                                            }
                                        />
                                        <RechartsTooltip
                                            formatter={(v, _n, item) => {
                                                const sec =
                                                    item?.payload?.secondsTotal;
                                                const label =
                                                    sec != null
                                                        ? formatCleaningDuration(
                                                              sec
                                                          )
                                                        : formatCleanTimeChartAxisTick(
                                                              v,
                                                              timeChartUnit
                                                          );
                                                return [label, ''];
                                            }}
                                        />
                                        <Legend
                                            wrapperStyle={{
                                                fontSize: compact ? 10 : 12,
                                            }}
                                        />
                                        <Line
                                            type="monotone"
                                            yAxisId="left"
                                            dataKey="value"
                                            stroke={lineColor}
                                            strokeWidth={3}
                                            dot={{ r: compact ? 3 : 4 }}
                                            activeDot={{ r: compact ? 5 : 6 }}
                                            name={timeSeriesLabel}
                                        />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                            <div className={`${pieColClass} ${rowMb}`}>
                                <ReportPieChart
                                    title={null}
                                    color={cardColor}
                                    segmentColors={pieSegmentColors}
                                    data={pieData}
                                    dataKey="value"
                                    customization={pieCustomization}
                                />
                            </div>
                        </div>
                    </ChartCard>
                </ChatCardContainer>
            </div>
        </div>
    );
}

CleaningReportChartsCard.propTypes = {
    cardColor: PropTypes.string.isRequired,
    chartColor: PropTypes.string,
    pieSegmentColors: PropTypes.arrayOf(PropTypes.string),
    list: PropTypes.arrayOf(
        // eslint-disable-next-line react/forbid-prop-types -- linha da API (campos extras ok)
        PropTypes.shape({
            report_id: PropTypes.string,
            task_name: PropTypes.string,
            mode: PropTypes.number,
            status: PropTypes.number,
            clean_time: PropTypes.number,
            clean_area: PropTypes.number,
            create_time: PropTypes.string,
        })
    ).isRequired,
    localeCode: PropTypes.string,
    periodDates: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
    compact: PropTypes.bool,
    initialPieField: PropTypes.oneOf([MODE_FILTER, STATUS_FILTER]),
    areaChartHeight: PropTypes.number,
};

CleaningReportChartsCard.defaultProps = {
    localeCode: 'pt_BR',
    chartColor: null,
    pieSegmentColors: null,
    periodDates: null,
    compact: false,
    initialPieField: MODE_FILTER,
    areaChartHeight: 220,
};
